import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getCustomers, getProducts, getQuotationById, createQuotation, updateQuotation } from '../../services/api';

const QuotationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    quotation_date: new Date().toISOString().split('T')[0],
    expiry_date: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0],
    notes: '',
    discount: 0,
    status: 'Draft',
    items: []
  });
  
  const [newItem, setNewItem] = useState({
    product_id: '',
    quantity: 1,
    unit_price: 0,
    discount_percentage: 0
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [customersResponse, productsResponse] = await Promise.all([
        getCustomers(),
        getProducts()
      ]);
      
      if (customersResponse.success) {
        setCustomers(customersResponse.data);
      }
      
      if (productsResponse.success) {
        setProducts(productsResponse.data);
      }
      
      if (isEditMode) {
        const quotationResponse = await getQuotationById(id);
        if (quotationResponse.success) {
          const quotation = quotationResponse.data;
          setFormData({
            customer_id: quotation.customer_id,
            quotation_date: quotation.quotation_date.split('T')[0],
            expiry_date: quotation.expiry_date.split('T')[0],
            notes: quotation.notes || '',
            discount: parseFloat(quotation.discount) || 0,
            status: quotation.status,
            items: quotation.items || []
          });
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const calculateItemTotal = (item) => {
    const subtotal = (item.quantity || 0) * (item.unit_price || 0);
    const discount = subtotal * ((item.discount_percentage || 0) / 100);
    return subtotal - discount;
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    const tax_rate = 15;
    const tax_amount = subtotal * (tax_rate / 100);
    const total = subtotal + tax_amount - (parseFloat(formData.discount) || 0);
    
    return {
      subtotal: subtotal.toFixed(2),
      tax_amount: tax_amount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const handleAddItem = () => {
    if (!newItem.product_id) {
      setError('Please select a product');
      return;
    }
    
    const selectedProduct = products.find(p => p.id === parseInt(newItem.product_id));
    if (!selectedProduct) {
      setError('Selected product not found');
      return;
    }
    
    const item = {
      product_id: newItem.product_id,
      product_name: selectedProduct.name,
      quantity: parseInt(newItem.quantity) || 1,
      unit_price: parseFloat(newItem.unit_price) || parseFloat(selectedProduct.price),
      discount_rate: parseFloat(newItem.discount_rate) || 0
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));
    
    setNewItem({
      product_id: '',
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0
    });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleProductChange = (e) => {
    const productId = e.target.value;
    setNewItem(prev => {
      const selectedProduct = products.find(p => p.id === parseInt(productId));
      return {
        ...prev,
        product_id: productId,
        unit_price: selectedProduct ? parseFloat(selectedProduct.price) : 0
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      setError('Please select a customer');
      return;
    }
    
    if (formData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const quotationData = {
        ...formData,
        customer_id: parseInt(formData.customer_id),
        discount: parseFloat(formData.discount) || 0,
        items: formData.items.map(item => ({
          product_id: parseInt(item.product_id),
          quantity: parseInt(item.quantity),
          unit_price: parseFloat(item.unit_price),
          discount_rate: parseFloat(item.discount_rate) || 0
        }))
      };
      
      let response;
      if (isEditMode) {
        response = await updateQuotation(id, quotationData);
      } else {
        response = await createQuotation(quotationData);
      }
      
      if (response.success) {
        setSuccess(isEditMode ? 'Quotation updated successfully!' : 'Quotation created successfully!');
        
        setTimeout(() => {
          navigate('/quotations');
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to save quotation');
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/quotations')}
        sx={{ mb: 3 }}
      >
        Back to Quotations
      </Button>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? 'Edit Quotation' : 'Create New Quotation'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Customer and Dates */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Customer</InputLabel>
                <Select
                  label="Customer"
                  value={formData.customer_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_id: e.target.value }))}
                  required
                >
                  <MenuItem value="">Select Customer</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name} - {customer.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Quotation Date"
                value={formData.quotation_date}
                onChange={(e) => setFormData(prev => ({ ...prev, quotation_date: e.target.value }))}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                value={formData.expiry_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Add Items Section */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Add Items
                  </Typography>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Product</InputLabel>
                        <Select
                          label="Product"
                          value={newItem.product_id}
                          onChange={handleProductChange}
                        >
                          <MenuItem value="">Select Product</MenuItem>
                          {products.map((product) => (
                            <MenuItem key={product.id} value={product.id}>
                              {product.name} - Stock: {product.quantity}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Quantity"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem(prev => ({ ...prev, quantity: e.target.value }))}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Unit Price (LKR)"
                        value={newItem.unit_price}
                        onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: e.target.value }))}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Discount %"
                        value={newItem.discount_percentage}
                        onChange={(e) => setNewItem(prev => ({ ...prev, discount_percentage: e.target.value }))}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={2}>
                      <Typography>
                        Total: LKR {calculateItemTotal(newItem).toFixed(2)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={1}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddItem}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Items Table */}
            {formData.items.length > 0 && (
              <Grid item xs={12}>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price (LKR)</TableCell>
                        <TableCell align="right">Discount %</TableCell>
                        <TableCell align="right">Line Total (LKR)</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{parseFloat(item.unit_price).toFixed(2)}</TableCell>
                          <TableCell align="right">{item.discount_percentage}%</TableCell>
                          <TableCell align="right">{calculateItemTotal(item).toFixed(2)}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveItem(index)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Status and Notes */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <MenuItem value="Draft">Draft</MenuItem>
                    <MenuItem value="Sent">Sent</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                    <MenuItem value="Expired">Expired</MenuItem>
                    <MenuItem value="Converted">Converted</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            {/* Totals */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quotation Summary
                  </Typography>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Subtotal:</Typography>
                    <Typography>LKR {totals.subtotal}</Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Discount:</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={formData.discount}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount: e.target.value }))}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ width: 100 }}
                    />
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>VAT (15%):</Typography>
                    <Typography>LKR {totals.tax_amount}</Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">Total:</Typography>
                    <Typography variant="h6" color="primary">
                      LKR {totals.total}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/quotations')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEditMode ? 'Update Quotation' : 'Create Quotation'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default QuotationForm;